import { useCallback, useEffect, useState } from "react";
import { PageContainer } from "../shared/components/PageContainer";
import Card from "@mui/material/Card";
import axios from "axios";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useFetchContext } from "../../contexts/FetchContext";
import { DocumentStatus, DocumentsData } from "../../interfaces/document";
import { Box, Button, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import DocumentCard from "../documents/components/DocumentCard";
import ConfirmationModal from "../shared/components/Modal/ConfirmationModal";
import { AxiosError } from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import SwipingModal from "../shared/components/SwipingModal";
import SerialNumberLocations from "../verify/products/components/SerialNumberLocations";
import { useSoundContext } from "../../contexts/SoundContext";

interface ProductItem {
  id: number;
  serialNumber: string;
  productCode: string;
}

interface GroupedProduct {
  productCode: string;
  quantity: number;
}

const Dispatch = () => {
  const { state, fetchData } = useFetchContext();
  const { play } = useSoundContext();

  const urlParams = new URLSearchParams(window.location.search);

  const uuidParam = urlParams.get("uuid");

  const [tableData, setTableData] = useState<Array<ProductItem>>([]);
  const [dispatchData, setDispatchData] = useState<DocumentsData | null>(null);
  const [showProductCode, setShowProductCode] = useState<string>("");

  const [inputValue, setInputValue] = useState<string>("");
  const [borderColorInput, setBorderColorInput] = useState<boolean>(false);

  const dataGrouped =
    dispatchData?.products &&
    Object.values(
      dispatchData.products.reduce<Record<string, GroupedProduct>>(
        (acc, row) => {
          if (!acc[row.productCode]) {
            acc[row.productCode] = { ...row, quantity: 1 };
          } else {
            acc[row.productCode].quantity += 1;
          }
          return acc;
        },
        {}
      )
    );

  const onPrintDispatch = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + "/documents/print-pdf/" + uuidParam,
        { responseType: "blob" } // !!!
      );
      window.open(URL.createObjectURL(response.data));
    } catch (error) {
      console.error("Errore durante il download del PDF:", error);
    }
  };

  const checkCanAdd = (productCode: string) => {
    const filteredData = (tableData?.filter(
      (item) => item.productCode === productCode
    )).length;

    const groupedItem = dataGrouped?.find(
      (item) => item.productCode === productCode
    )?.quantity;

    if (groupedItem && groupedItem > filteredData) {
      return true;
    }

    toast.error("Errore, limite prodotto raggiunto");

    return false;
  };

  const addToTableData = useCallback(async () => {
    if (inputValue !== "") {
      try {
        const response = await fetchData(
          `/documents/check-serial-number-availability?documentId=${dispatchData?.id}&serialNumber=${inputValue}`,
          "GET"
        );

        if (response?.data?.statusCode === 400 || !response) {
          setBorderColorInput(true);
          play("success");
        } else {
          const product = response?.data;
          if (product) {
            const isDuplicate = tableData.some(
              (row) => row.serialNumber === product.serialNumber
            );
            if (!isDuplicate) {
              if (checkCanAdd(product.productCode)) {
                setTableData((prevTableData) => [
                  ...prevTableData,
                  {
                    id: prevTableData.length + 1,
                    serialNumber: product.serialNumber,
                    productCode: product.productCode,
                  },
                ]);
                setInputValue("");
                setBorderColorInput(false);
                play("success");
              } else {
                setBorderColorInput(true);
                play("error");
              }
            } else {
              setBorderColorInput(true);
              play("error");
            }
          } else {
            setBorderColorInput(true);
          }
        }
      } catch (error) {
        setBorderColorInput(true);
        play("error");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const response = await fetchData(`/documents/${uuidParam}`);
        setDispatchData(response?.data);

        if (response?.data?.status === DocumentStatus.CLOSED) {
          setTableData(
            response?.data?.products?.map((el: any, index: number) => {
              return {
                id: index + 1,
                serialNumber: el.serialNumber,
                productCode: el.productCode,
              };
            })
          );
        }
      } catch (error) {}
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardStyle = {
    marginBottom: "16px",
  };

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const openConfirmationModal = () => {
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const handleConfirmation = async () => {
    try {
      const response = await fetchData(`/documents/close-dispatch`, "POST", {
        documentId: dispatchData?.id,
        products: tableData.map((el) => {
          return {
            productCode: el.productCode,
            serialNumber: el.serialNumber,
          };
        }),
      });
      if (response?.data?.status === DocumentStatus.CLOSED) {
        setDispatchData(response?.data);
        toast.success("Evasione completata");
      } else {
        toast.error("Errore con la gestione di questo documento");
      }
      closeConfirmationModal();
    } catch (error) {
      const axiosError = error as AxiosError;
      toast.error(
        axiosError?.message ?? "Errore con la communicazione con il server"
      );
    }
  };

  return (
    <PageContainer>
      <Box
        sx={{
          padding: 1,
        }}
      >
        {dispatchData && (
          <DocumentCard elements={dispatchData} sx={cardStyle} showAddress />
        )}

        {dispatchData?.status !== DocumentStatus.CLOSED && (
          <Card sx={cardStyle}>
            <CardContent>
              <TableContainer>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  PRODOTTI
                </Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Prodotto</TableCell>
                      <TableCell>Quantità</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataGrouped?.map((groupedRow, index) => (
                      <TableRow
                        key={index}
                        onClick={() =>
                          setShowProductCode(groupedRow.productCode)
                        }
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{groupedRow.productCode}</TableCell>
                        <TableCell>{groupedRow.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
        {dispatchData?.status !== DocumentStatus.CLOSED && (
          <Card sx={cardStyle}>
            <CardContent>
              <TextField
                autoFocus
                label="Inserisci matricola"
                value={inputValue}
                onChange={(e) => {
                  setBorderColorInput(false);
                  setInputValue(e.target.value);
                }}
                onKeyDown={(event: any) => {
                  if (event.key === "Enter") {
                    addToTableData();
                    event.preventDefault();
                  }
                }}
                sx={{ width: "100%", mb: "15px" }}
                error={borderColorInput ?? false}
              />
            </CardContent>
          </Card>
        )}

        <Card sx={cardStyle}>
          <CardContent>
            <TableContainer>
              {dispatchData?.status !== DocumentStatus.CLOSED && (
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  MATRICOLE INSERITE ({tableData.length}/
                  {dispatchData?.products?.length})
                </Typography>
              )}

              {tableData?.length ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Matricola</TableCell>
                      <TableCell>Prodotto</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.serialNumber}</TableCell>
                        <TableCell>{row.productCode}</TableCell>
                        <TableCell>
                          {dispatchData?.status !== DocumentStatus.CLOSED ? (
                            <IconButton
                              aria-label="delete"
                              onClick={() =>
                                setTableData((prevTableData) => {
                                  const updatedTableData = prevTableData
                                    .filter((row2) => row2.id !== row.id)
                                    .map((row2, index) => ({
                                      ...row2,
                                      id: index + 1,
                                    }));
                                  return updatedTableData;
                                })
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          ) : (
                            ""
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                ""
              )}
            </TableContainer>
          </CardContent>
        </Card>
        <div
          style={{
            textAlign: "right",
            marginTop: "16px",
            marginBottom: "50px",
          }}
        >
          {dispatchData?.status !== DocumentStatus.CLOSED ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={tableData.length !== dispatchData?.products?.length}
              onClick={openConfirmationModal}
            >
              EVADI
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={tableData.length !== dispatchData?.products?.length}
              onClick={onPrintDispatch}
            >
              STAMPA
            </Button>
          )}
        </div>
      </Box>
      <SwipingModal
        isOpen={showProductCode !== ""}
        title={`Ubicazione matricole ${showProductCode}`}
        content={<SerialNumberLocations productCode={showProductCode} />}
        onClose={() => setShowProductCode("")}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={handleConfirmation}
        isLoading={state?.isLoading}
      />
    </PageContainer>
  );
};

export default Dispatch;
